import React from "react";
import { useTranslation } from "react-i18next";
import {
  IMPORT_REPORT_KINDS,
  IMPORT_REPORT_OBJECTS,
  formatImportIssue,
  importIssueRow,
  normalizeImportReport,
} from "./importReport";
import "./ImportReport.css";

/**
 * One list of import issues. The SAME component renders both channels —
 * `severity="error"` (red, ✕) and `severity="warning"` (amber, ⚠) — so the
 * two never drift apart visually or in test hooks:
 *   ul  → data-testid="library-import-errors" | "library-import-warnings"
 *   li  → data-testid="library-import-error-item" | "library-import-warning-item"
 *         data-row="<row number>" when the issue points at a row.
 */
export function ImportIssueList({ severity = "error", items, title, testIdPrefix = "library-import" }) {
  const { t } = useTranslation();
  const list = Array.isArray(items) ? items : [];
  if (list.length === 0) return null;
  const isWarning = severity === "warning";
  const heading =
    title ??
    (isWarning
      ? t("library.importReport.warningsTitle", {
          count: list.length,
          defaultValue: `${list.length} warning(s)`,
        })
      : t("library.importReport.errorsTitle", {
          count: list.length,
          defaultValue: `${list.length} error(s)`,
        }));

  return (
    <div
      className={`import-report__issues import-report__issues--${severity}`}
      data-testid={`${testIdPrefix}-${isWarning ? "warnings" : "errors"}`}
      role={isWarning ? "status" : "alert"}
    >
      <div className="import-report__issues-head">
        <i
          className={isWarning ? "fas fa-exclamation-triangle" : "fas fa-times-circle"}
          aria-hidden="true"
        />
        <strong>{heading}</strong>
        {isWarning && (
          <span className="import-report__issues-note">
            {t("library.importReport.warningsNote", "Warnings never stop the import.")}
          </span>
        )}
      </div>
      <ul className="import-report__issues-list">
        {list.map((issue, index) => {
          const row = importIssueRow(issue);
          return (
            <li
              key={`${row ?? "-"}-${index}`}
              className="import-report__issue"
              data-testid={`${testIdPrefix}-${isWarning ? "warning" : "error"}-item`}
              data-row={row ?? undefined}
              data-type={issue?.errorType ?? undefined}
            >
              {formatImportIssue(issue, t)}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/**
 * Per-object created / updated / unchanged counters. Only the object types the
 * backend counted are shown (a legacy report shows nothing). Each number is
 * addressable as data-testid="library-import-counter-<object>-<kind>".
 */
export function ImportCounters({ counters, dryRun = false, testIdPrefix = "library-import" }) {
  const { t } = useTranslation();
  const objects = IMPORT_REPORT_OBJECTS.filter((object) => counters && counters[object]);
  if (objects.length === 0) return null;

  return (
    <div className="import-report__counters" data-testid={`${testIdPrefix}-counters`}>
      <div className="import-report__counters-head">
        <i className="fas fa-list-ol" aria-hidden="true" />
        <strong>
          {dryRun
            ? t("library.importReport.countersTitleDryRun", "What this import will do")
            : t("library.importReport.countersTitle", "What this import did")}
        </strong>
      </div>
      <table className="import-report__counters-table">
        <thead>
          <tr>
            <th />
            {IMPORT_REPORT_KINDS.map((kind) => (
              <th key={kind}>{t(`library.importReport.kind.${kind}`, kind)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {objects.map((object) => (
            <tr key={object} data-testid={`${testIdPrefix}-counter-row-${object}`}>
              <th scope="row">{t(`library.importReport.object.${object}`, object)}</th>
              {IMPORT_REPORT_KINDS.map((kind) => (
                <td
                  key={kind}
                  className={`import-report__counter import-report__counter--${kind}`}
                  data-testid={`${testIdPrefix}-counter-${object}-${kind}`}
                >
                  {counters[object][kind]}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * The whole import result: counters, then errors, then warnings — in that
 * order so a successful import leads with what it did, and a rejected one
 * leads with why. Accepts a raw backend report or an already-normalized one;
 * renders nothing for a value that is not a report.
 *
 * Props:
 *   report        — CsvValidationReportDTO (or legacy `{ errors }`)
 *   showErrors    — default true
 *   showWarnings  — default true
 *   showCounters  — default true; counters are hidden on a rejected file
 *                   (nothing was imported) unless `forceCounters`
 *   testIdPrefix  — default "library-import"
 */
export default function ImportReportPanel({
  report,
  showErrors = true,
  showWarnings = true,
  showCounters = true,
  forceCounters = false,
  testIdPrefix = "library-import",
  className = "",
}) {
  const normalized = normalizeImportReport(report);
  if (!normalized) return null;
  const countersVisible = showCounters && normalized.hasCounters && (normalized.valid || forceCounters);
  const hasAnything =
    countersVisible ||
    (showErrors && normalized.errors.length > 0) ||
    (showWarnings && normalized.warnings.length > 0);
  if (!hasAnything) return null;

  return (
    <div className={`import-report ${className}`.trim()} data-testid={`${testIdPrefix}-report`}>
      {countersVisible && (
        <ImportCounters
          counters={normalized.counters}
          dryRun={normalized.dryRun}
          testIdPrefix={testIdPrefix}
        />
      )}
      {showErrors && (
        <ImportIssueList severity="error" items={normalized.errors} testIdPrefix={testIdPrefix} />
      )}
      {showWarnings && (
        <ImportIssueList severity="warning" items={normalized.warnings} testIdPrefix={testIdPrefix} />
      )}
    </div>
  );
}
